import { Timestamp } from 'firebase/firestore';

/**
 * ตรวจสอบว่า Package Subscription หมดอายุหรือยัง
 * @param {object} subscription - Subscription object จาก Firestore
 * @returns {object} { isExpired: boolean, isValid: boolean, daysRemaining: number | null }
 */
export const checkPackageExpiration = (subscription) => {
  if (!subscription) {
    return { isExpired: true, isValid: false, daysRemaining: null };
  }

  // ตรวจสอบ status
  if (subscription.status !== 'active') {
    return { isExpired: true, isValid: false, daysRemaining: null };
  }

  // ถ้าไม่มี endDate = unlimited (ไม่หมดอายุ)
  if (!subscription.endDate) {
    return { isExpired: false, isValid: true, daysRemaining: null }; // null = unlimited
  }

  // แปลง endDate เป็น Date object
  let endDate = null;
  try {
    if (subscription.endDate instanceof Date) {
      endDate = subscription.endDate;
    } else if (subscription.endDate.toDate && typeof subscription.endDate.toDate === 'function') {
      endDate = subscription.endDate.toDate();
    } else if (subscription.endDate.seconds) {
      // Firestore Timestamp format { seconds, nanoseconds }
      endDate = new Date(subscription.endDate.seconds * 1000);
    } else if (typeof subscription.endDate === 'string' || typeof subscription.endDate === 'number') {
      endDate = new Date(subscription.endDate);
    }

    if (!endDate || isNaN(endDate.getTime())) {
      // ถ้าแปลงไม่ได้ = unlimited
      return { isExpired: false, isValid: true, daysRemaining: null };
    }
  } catch (error) {
    console.error('Error parsing endDate:', error);
    // ถ้าแปลงไม่ได้ = unlimited
    return { isExpired: false, isValid: true, daysRemaining: null };
  }

  // ตรวจสอบว่าหมดอายุหรือยัง
  const now = new Date();
  const isExpired = endDate < now;

  if (isExpired) {
    return { isExpired: true, isValid: false, daysRemaining: 0 };
  }

  // คำนวณจำนวนวันที่เหลือ
  const diffTime = endDate - now;
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return { isExpired: false, isValid: true, daysRemaining };
};

/**
 * ตรวจสอบว่า Package Subscription ยังใช้งานได้หรือไม่ (รวมถึงตรวจสอบ expiration)
 * @param {object} subscription - Subscription object จาก Firestore
 * @returns {boolean} true ถ้ายังใช้งานได้
 */
export const isPackageValid = (subscription) => {
  const check = checkPackageExpiration(subscription);
  return check.isValid && !check.isExpired;
};

/**
 * ตรวจสอบและอัพเดท status ของ subscription ถ้าหมดอายุ
 * @param {object} subscription - Subscription object จาก Firestore
 * @param {object} subscriptionRef - Firestore document reference (optional)
 * @returns {object} Subscription object ที่อัพเดทแล้ว (ถ้ามีการอัพเดท)
 */
export const validateAndUpdateSubscription = async (subscription, subscriptionRef = null) => {
  if (!subscription) return null;

  const check = checkPackageExpiration(subscription);
  
  // ถ้าหมดอายุและยังไม่ได้อัพเดท status
  if (check.isExpired && subscription.status === 'active' && subscriptionRef) {
    try {
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(subscriptionRef, {
        status: 'expired',
        updatedAt: new Date()
      });
      
      return {
        ...subscription,
        status: 'expired'
      };
    } catch (error) {
      console.error('Error updating subscription status:', error);
    }
  }

  return subscription;
};

/**
 * ตรวจสอบ Package และ return package info พร้อม expiration check
 * @param {object} subscription - Subscription object
 * @returns {object} { packageType, isValid, isExpired, daysRemaining, status }
 */
export const getPackageInfo = (subscription) => {
  if (!subscription) {
    return {
      packageType: 'free',
      isValid: false,
      isExpired: true,
      daysRemaining: null,
      status: 'expired'
    };
  }

  const expirationCheck = checkPackageExpiration(subscription);
  
  return {
    packageType: subscription.packageType || 'free',
    isValid: expirationCheck.isValid && !expirationCheck.isExpired,
    isExpired: expirationCheck.isExpired,
    daysRemaining: expirationCheck.daysRemaining,
    status: subscription.status || 'expired'
  };
};

